import './SubscriptionAuthor.sass';
import '../Profile/Profile.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Header, Placeholder, ScrollView } from '$uikit';
import {
  classNames as cn,
  formatCurrency,
  inclineUserName,
  rnbr,
  statReachGoal,
  subscriptionDiscountPrice,
} from '$utils';
import { Api } from '$api';
import { AuthPopoverState, CurrencyEnum, SubscriptionType } from '$store/models';
import { mainActions } from '$store/main';
import { toastActions } from '$store/toast';
import {
  useCurrentLang,
  useCurrentUserInfo,
  useDesktop,
  useTranslator,
  useUserInfo,
} from '$hooks';
import { Link, useRoute } from 'react-router5';
import { useDispatch, useSelector } from 'react-redux';
import { Loader, SubscriptionIcon, UnitName, UnitPhoto } from '$shared/components';
import {
  IcSubscriptionDirect28,
  IcSubscriptionExtra28,
  IcSubscriptionMedia28,
  IcSubscriptionMore28,
  IcSuccess72,
} from '$assets';
import { goBack } from '$navigation/helper';
import { balancePayActions } from '$store/balancePay';
import { BalancePayAction } from '$store/balancePay/interface';
import { Subscription } from '$core/Subscription/Subscription';
import { RESOLVE } from '$shared/constants/pages';
import { AuthPopoverSimple } from '$core/AuthPopover/AuthPopover';
import { authSelector } from '$store/auth';
import { profileActions } from '$store/profile';
import { setSubscriptionAuthor } from '$utils/subscriptionAuthor';
import { useHasSubscription } from '$utils/useHasSubscription';
import { useGetAuthorId } from '$utils/useGetAuthorId';

export const SubscriptionAuthor: FC = () => {
  const route = useRoute();
  const t = useTranslator();
  const authorId = useGetAuthorId();
  const hasSubscription = useHasSubscription(authorId);
  const memoHasSubscription = useMemo(() => hasSubscription, []);
  const transitionFromProfile = route.route.meta?.options?.transitionFromProfile || false;
  const [loadingStatus, setLoadingStatus] = useState('loading');
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionDiscount, setSubscriptionDiscount] = useState(0);
  const { isLogged } = useSelector(authSelector);
  const author = useUserInfo(authorId);
  const [price, setPrice] = useState({ ton: 0, eur: 0 });
  const [cover, setCover] = useState<any>(null);
  const [sponsorsDescription, setSponsorsDescription] = useState('');
  const [contentCount, setContentCount] = useState({ photos: 0, videos: 0 });
  const isDesktop = useDesktop();
  const dispatch = useDispatch();
  const currentLang = useCurrentLang();
  const curUser = useCurrentUserInfo();
  const [showGift, setShowGift] = useState(false);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (authorId === 0 || author) {
      // @ts-ignore
      if (isLogged && route?.route.params.open_pay && !window.isPayModalShown) {
        // @ts-ignore
        window.isPayModalShown = true;
        handleSubscription();
      }
    }
  }, [author, isLoading]);

  const loadData = useCallback(() => {
    setLoadingStatus('loading');
    Api.get(`/balance/subscription/author/${authorId}`)
      .then((data: any) => {
        if (data.canActivateTrial && !!curUser) {
          setShowGift(true);
        }

        setSubscriptionAuthor(data.user);
        setPrice({
          ton: data.price,
          eur: data.eurPrice,
        });
        setSponsorsDescription(data.sponsorsDescription);
        if (data.coverUrl) {
          if (transitionFromProfile) {
            setCover(null);
          } else {
            setCover({
              url: data.coverUrl,
              thumbnail: data.coverThumbnail,
            });
          }
        }
        if (data.user) {
          dispatch(mainActions.setUser(data.user));
        }
        setContentCount({
          photos: data.photosCnt,
          videos: data.videosCnt,
        });
        setSubscriptionDiscount(data.subscriptionDiscount);
        setLoadingStatus('loaded');
      })
      .catch((e) => {
        setLoadingStatus('failed');
        dispatch(toastActions.setToastFail(e.message));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [authorId, curUser]);

  useEffect(() => {
    loadData();
    statReachGoal('sponsor_subscription_value_view');
  }, []);

  const handleSubscription = useCallback(() => {
    statReachGoal('sponsor_subscription_value_btn_click');

    if (!isLogged) {
      dispatch(profileActions.setAuthPopoverState(AuthPopoverState.LOGIN));
      return;
    }

    dispatch(
      balancePayActions.open({
        amount: subscriptionDiscount
          ? subscriptionDiscountPrice(+price.ton, subscriptionDiscount)
          : +price.ton,
        action: BalancePayAction.SUBSCRIPTION_PERSONAL,
        objectId: authorId,
      }),
    );
  }, [authorId, price, isLogged, subscriptionDiscount]);

  const title = useMemo(() => {
    if (isLoading) {
      return '';
    }

    return t('subscription_personal_title', {
      name: inclineUserName(author, 'accusative', currentLang.key).full,
    });
  }, [t, currentLang, isLoading]);

  const description = useMemo(() => {
    return (
      sponsorsDescription ||
      (author
        ? t('subscription_private_caption', {
            name: inclineUserName(author, 'instrumental', currentLang.key).first,
          })
        : '')
    );
  }, [t, author, sponsorsDescription]);

  const coverImage = useMemo(() => {
    if (!cover) {
      return null;
    }

    return cover.url && cover.thumbnail
      ? [cover.url, cover.thumbnail].map((url) => `url(${url})`).join(', ')
      : null;
  }, [cover]);

  if (isLoading) {
    return <Loader isWrapped />;
  }

  if (authorId && author && author.verified === 0) {
    // TODO: Remove hack
    return <Subscription type={SubscriptionType.Author} />;
  }

  if (memoHasSubscription) {
    return (
      <Placeholder
        centered
        title={t('subscription_success_title')}
        icon={<IcSuccess72 />}
        actions={
          <Link
            routeName={RESOLVE}
            routeParams={{ domain: 'id' + authorId }}
            routeOptions={{ tab: 'sponsored' }}
          >
            <Button>{t('subscription_success_button_go_to_posts')}</Button>
          </Link>
        }
      >
        {t('subscription_success_author_caption', {
          name: inclineUserName(author, 'genitive', currentLang.key).full,
        })}
      </Placeholder>
    );
  }

  if (!price.ton) {
    return !isLoading ? (
      <Placeholder
        centered
        icon={
          <SubscriptionIcon
            size="large"
            type={SubscriptionType.Author}
            authorId={author.id}
          />
        }
        iconSize={82}
        title={<UnitName unit={author} />}
        actions={
          <Button onClick={() => goBack()} size="small">
            {t('back')}
          </Button>
        }
      >
        {t('subscription_private_failed_not_activated')}
      </Placeholder>
    ) : (
      <Loader isWrapped />
    );
  }

  return (
    <div className={cn('SubscriptionAuthor', { hasCover: !!cover })}>
      {loadingStatus === 'loaded' ? (
        <ScrollView
          footer={
            <>
              <Button onClick={handleSubscription} fillColor="red">
                {subscriptionDiscount
                  ? t('subscription_personal_button_with_discount', {
                      price: formatCurrency(
                        subscriptionDiscountPrice(price.ton, subscriptionDiscount),
                        CurrencyEnum.EUR,
                      ),
                    })
                  : t('subscription_personal_button_with_price', {
                      price: formatCurrency(price.ton, CurrencyEnum.EUR),
                    })}
              </Button>
              <p className="Paywall__trialSubscription">
                {subscriptionDiscount
                  ? t('subscription_personal_button_description_with_discount', {
                      price: formatCurrency(price.ton, CurrencyEnum.EUR),
                    })
                  : t('subscription_personal_button_description')}
              </p>
            </>
          }
        >
          <Header showBackButton isTransparent={!!cover} />
          {cover && (
            <div
              className={cn('Profile__cover visible')}
              style={coverImage ? { backgroundImage: coverImage } : undefined}
            />
          )}
          <UnitPhoto
            unit={author}
            size={isDesktop ? 'extra-large' : 'large'}
            nftIndicator={false}
            onlineIndicator={false}
          />
          <div className="SubscriptionAuthor__content">
            <h1 className="SubscriptionAuthor__title">{title}</h1>
            <p className="SubscriptionAuthor__description">{rnbr(description)}</p>
            {showGift && (
              <div className="SubscriptionAuthor__list" style={{ marginBottom: 16 }}>
                <div className="SubscriptionAuthor__item">
                  <div className="SubscriptionAuthor__item__icon">
                    <SubscriptionIcon
                      type={SubscriptionType.Adult}
                      authorId={0}
                      size="list-icon"
                    />
                  </div>
                  <div className="SubscriptionAuthor__item__title">
                    {t('subscription_personal_free_erotic_title')}
                  </div>
                  <div className="SubscriptionAuthor__item__description">
                    {t('subscription_personal_free_erotic_caption')}
                  </div>
                </div>
              </div>
            )}
            <div className="SubscriptionAuthor__list">
              <div className="SubscriptionAuthor__item">
                <div className="SubscriptionAuthor__item__icon">
                  <IcSubscriptionMedia28 />
                </div>
                <div className="SubscriptionAuthor__item__title">
                  {contentCount.videos && contentCount.photos
                    ? [
                        t('subscription_personal_list_item_1_title_only_videos', {
                          count: contentCount.videos,
                        }),
                        t('subscription_personal_list_item_1_title_only_photos', {
                          count: contentCount.photos,
                        }),
                      ].join(t('subscription_personal_list_item_1_title_and'))
                    : contentCount.videos
                    ? t('subscription_personal_list_item_1_title_only_videos', {
                        count: contentCount.videos,
                      })
                    : contentCount.photos
                    ? t('subscription_personal_list_item_1_title_only_photos', {
                        count: contentCount.photos,
                      })
                    : t('subscription_personal_list_item_1_title')}
                </div>
                <div className="SubscriptionAuthor__item__description">
                  {t('subscription_personal_list_item_1_caption')}
                </div>
              </div>
              <div className="SubscriptionAuthor__item">
                <div className="SubscriptionAuthor__item__icon">
                  <IcSubscriptionDirect28 />
                </div>
                <div className="SubscriptionAuthor__item__title">
                  {t('subscription_personal_list_item_2_title')}
                </div>
                <div className="SubscriptionAuthor__item__description">
                  {t('subscription_personal_list_item_2_caption', {
                    name: inclineUserName(author, 'instrumental', currentLang.key).first,
                  })}
                </div>
              </div>
              <div className="SubscriptionAuthor__item">
                <div className="SubscriptionAuthor__item__icon">
                  <IcSubscriptionExtra28 />
                </div>
                <div className="SubscriptionAuthor__item__title">
                  {t('subscription_personal_list_item_3_title')}
                </div>
                <div className="SubscriptionAuthor__item__description">
                  {t('subscription_personal_list_item_3_caption', {
                    name: inclineUserName(author, 'genitive', currentLang.key).first,
                  })}
                </div>
              </div>
              <div className="SubscriptionAuthor__item">
                <div className="SubscriptionAuthor__item__icon">
                  <IcSubscriptionMore28 />
                </div>
                <div className="SubscriptionAuthor__item__title">
                  {t('subscription_personal_list_item_4_title')}
                </div>
                <div className="SubscriptionAuthor__item__description">
                  {t('subscription_personal_list_item_4_caption')}
                </div>
              </div>
            </div>
          </div>
        </ScrollView>
      ) : loadingStatus === 'failed' ? (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadData}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      ) : loadingStatus === 'loading' ? (
        <Loader isWrapped />
      ) : null}
      <AuthPopoverSimple />
    </div>
  );
};
